import { useEffect, useMemo } from 'react';
import { pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { useIsAdmin } from 'js/hooks/user';
import { useLocation } from 'react-router-dom';
import { useAuthSelector, useSocketSelector, useUserSelector } from 'js/store/hooks';
import { useRemoveQueryParams } from 'js/hooks/navigate';

import * as actions from '../store/actions';
import * as urlUtils from 'js/utils/url.util';
import { useLoginBySSO } from './useLoginBySSO';
import { useLoginRequest } from './useLoginRequest';
import { useLoginByGetParams } from './useLoginByGetParams';
import { useLoginByGoogleToken } from './useLoginByGoogleToken';

export const useAutoLogin = () => {
    const isAdmin = useIsAdmin();
    const location = useLocation();
    const request = useLoginRequest();
    const loginBySSO = useLoginBySSO();
    const dispatch = useDispatch();
    const loginByGetParams = useLoginByGetParams();
    const removeQueryParams = useRemoveQueryParams();
    const loginByGoogleToken = useLoginByGoogleToken();
    const {
        username,
        temptoken,
        tempToken,
        externaltoken,
        externalToken,
        googleExternalLoginToken,
        skipAutoLogin
    } = useMemo(() => urlUtils.getURLParams(location.search), [location.search]);
    const { isLoggedIn } = useUserSelector(pick(['isLoggedIn']));
    const {
        isLoginInProcess,
        isLogoutInProcess,
        wasSsoAttempted
    } = useAuthSelector(pick(['isLoginInProcess', 'isLogoutInProcess', 'wasSsoAttempted']));
    const { connected, isError: isSocketConnectionError } = useSocketSelector(pick(['connected', 'isError']));

    useEffect(() => {
        if (isAdmin || isSocketConnectionError) {
            dispatch(actions.setAutoLoginCompleted());

            return;
        }

        if (!connected || isLoggedIn || isLoginInProcess || isLogoutInProcess || !request) {
            return;
        }

        if (username && (tempToken || temptoken)) {
            removeQueryParams(['username', 'temptoken', 'tempToken']);
            loginByGetParams(username, tempToken || temptoken);
        } else if (username && (externalToken || externaltoken)) {
            removeQueryParams(['username', 'externaltoken', 'externalToken']);
            loginByGetParams(username, externalToken || externaltoken, 'externalToken');
        } else if (googleExternalLoginToken) {
            removeQueryParams(['googleExternalLoginToken']);
            loginByGoogleToken({ token: decodeURIComponent(googleExternalLoginToken), isAutoLogin: true });
        } else if (!wasSsoAttempted && !skipAutoLogin) {
            loginBySSO();
        } else if (skipAutoLogin) {
            dispatch(actions.setAutoLoginCompleted());
        }
    }, [
        connected,
        isAdmin,
        isLoggedIn,
        isSocketConnectionError,
        request,
        username,
        temptoken,
        tempToken,
        externaltoken,
        externalToken,
        wasSsoAttempted,
        isLoginInProcess,
        isLogoutInProcess,
        removeQueryParams,
        loginByGoogleToken,
        loginByGetParams,
        skipAutoLogin,
        loginBySSO,
        dispatch,
        googleExternalLoginToken
    ]);
};
